/**
 * Admin Authentication Service
 * Handles admin login, session management, and rate limiting
 */

import { AdminSession, LoginAttempt } from '../types';

const SESSION_KEY = 'playhub_admin_session';
const ATTEMPTS_KEY = 'playhub_admin_login_attempts';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes
const MAX_ATTEMPTS = 5;
const RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute

export class AdminAuthService {
  private static instance: AdminAuthService | null = null;

  static getInstance(): AdminAuthService {
    if (!AdminAuthService.instance) {
      AdminAuthService.instance = new AdminAuthService();
    }
    return AdminAuthService.instance;
  }

  /**
   * Hash a string using SHA-256 via Web Crypto API
   */
  private async hashPassword(password: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get the expected admin username from env vars
   */
  private getExpectedUsername(): string {
    return process.env.REACT_APP_ADMIN_USERNAME || 'admin';
  }

  /**
   * Get the expected password hash
   * Must be set via REACT_APP_ADMIN_PASSWORD_HASH at build time
   */
  private getExpectedPasswordHash(): string {
    if (process.env.REACT_APP_ADMIN_PASSWORD_HASH) {
      return process.env.REACT_APP_ADMIN_PASSWORD_HASH;
    }
    // No hash configured — login will always fail
    return 'NO_HASH_CONFIGURED';
  }

  /**
   * Check if rate limited
   */
  isRateLimited(): boolean {
    const attempts = this.getRecentAttempts();
    const recentFailed = attempts.filter(
      a => !a.success && Date.now() - a.timestamp < RATE_LIMIT_WINDOW
    );
    return recentFailed.length >= MAX_ATTEMPTS;
  }

  /**
   * Get seconds until rate limit resets
   */
  getRateLimitResetSeconds(): number {
    const attempts = this.getRecentAttempts();
    const recentFailed = attempts.filter(
      a => !a.success && Date.now() - a.timestamp < RATE_LIMIT_WINDOW
    );
    if (recentFailed.length === 0) return 0;
    const oldest = Math.min(...recentFailed.map(a => a.timestamp));
    const resetAt = oldest + RATE_LIMIT_WINDOW;
    return Math.max(0, Math.ceil((resetAt - Date.now()) / 1000));
  }

  /**
   * Attempt to log in with admin credentials
   */
  async login(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    // Check rate limit
    if (this.isRateLimited()) {
      const seconds = this.getRateLimitResetSeconds();
      return {
        success: false,
        error: `Too many failed attempts. Try again in ${seconds} seconds.`
      };
    }

    const expectedUsername = this.getExpectedUsername();
    const expectedHash = this.getExpectedPasswordHash();
    let isValid = false;
    
    if (username === expectedUsername) {
      try {
        const inputHash = await this.hashPassword(password);
        // Timing-safe comparison to prevent timing attacks
        isValid = inputHash.length === expectedHash.length &&
          inputHash.split('').every((char, i) => char === expectedHash[i]);
      } catch {
        // crypto.subtle unavailable — reject login
        this.recordAttempt(false);
        return { success: false, error: 'Authentication unavailable. Ensure HTTPS.' };
      }
    }

    if (isValid) {
      this.recordAttempt(true);
      const session = this.createSession(username);
      this.saveSession(session);
      return { success: true };
    }

    // Failed login
    this.recordAttempt(false);
    return { success: false, error: 'Invalid username or password.' };
  }

  /**
   * Log out and clear session
   */
  logout(): void {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Check if there's a valid admin session
   */
  isAuthenticated(): boolean {
    const session = this.getSession();
    if (!session) return false;
    if (Date.now() > session.expiresAt) {
      this.logout();
      return false;
    }
    return true;
  }

  /**
   * Get the current admin session
   */
  getSession(): AdminSession | null {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (!stored) return null;
      return JSON.parse(stored) as AdminSession;
    } catch {
      return null;
    }
  }

  /**
   * Refresh the session expiry (extend by 30 minutes)
   */
  refreshSession(): void {
    const session = this.getSession();
    if (session) {
      session.expiresAt = Date.now() + SESSION_DURATION;
      this.saveSession(session);
    }
  }

  private createSession(username: string): AdminSession {
    const token = this.generateToken();
    return {
      token,
      username,
      expiresAt: Date.now() + SESSION_DURATION,
      createdAt: Date.now()
    };
  }

  private generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  private saveSession(session: AdminSession): void {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
      // Ignore storage errors
    }
  }

  private getRecentAttempts(): LoginAttempt[] {
    try {
      const stored = localStorage.getItem(ATTEMPTS_KEY);
      if (!stored) return [];
      const attempts: LoginAttempt[] = JSON.parse(stored);
      // Only keep attempts from the last minute
      return attempts.filter(a => Date.now() - a.timestamp < RATE_LIMIT_WINDOW);
    } catch {
      return [];
    }
  }

  private recordAttempt(success: boolean): void {
    try {
      const attempts = this.getRecentAttempts();
      attempts.push({ timestamp: Date.now(), success });
      localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(attempts));
    } catch {
      // Ignore storage errors
    }
  }
}
